import getAccountDisplayName from './AccountDisplayName'
import { GlobalStore } from '../../core/stores/GlobalStore'

import type { Account } from '../../core'
import { AccountType, AccountTypeName } from '../../core'

export interface MappedAccounts {
  [key: string]: MappedAccount[]
}

export interface MappedAccount {
  accountNumber: string
  name: string
  guid: string
}

export interface MappedOptions {
  [key: string]: boolean
}

// Sets up the collection of selected account guids into a mapped object
export const setupMappedSelectedOptions = (accounts: Account[], selectedAccountGuids: string[]) => {
  return accounts.reduce((accumulator: MappedOptions, account) => {
    accumulator[account.guid] = selectedAccountGuids?.includes(account.guid)
    return accumulator
  }, {})
}

// Creates a mapped collection of accounts that are grouped together by the account type
export const setupMappedAccounts = (
  accounts: Account[],
  closeContentCopy: string,
  globalStore?: GlobalStore,
) => {
  return accounts.reduce((accumulator: MappedAccounts, account) => {
    const accountType = AccountTypeName[account.account_type]
    const displayName = getAccountDisplayName(account, {
      allowNickname: globalStore?.appConfig.config?.show_account_nicknames_in_master,
    })
    const newAccount = {
      accountNumber: account.account_number,
      name: `${displayName} ${account.is_closed ? `(${closeContentCopy})` : ''}`,
      guid: account.guid,
    } as MappedAccount

    if (Object.prototype.hasOwnProperty.call(accumulator, accountType)) {
      accumulator[accountType] = accumulator[accountType].concat(newAccount)
    } else {
      accumulator[accountType] = [newAccount]
    }

    return accumulator
  }, {})
}

export function categorizeAccounts(accounts: Account[]): {
  heldAccounts: Account[]
  externalAccounts: Account[]
} {
  const { heldAccounts, externalAccounts } = accounts.reduce(
    (acc, account) => {
      if (account.member_is_managed_by_user) acc.externalAccounts.push(account)
      else acc.heldAccounts.push(account)
      return acc
    },
    { heldAccounts: [] as Account[], externalAccounts: [] as Account[] },
  )

  return {
    heldAccounts: sortByTypePriority(heldAccounts),
    externalAccounts: sortByTypePriority(externalAccounts),
  }
}

export function getDisplayAccounts(accounts: Account[], maxAccounts: number): Account[] {
  const { heldAccounts, externalAccounts } = categorizeAccounts(accounts)

  const result = heldAccounts.slice(0, maxAccounts)

  if (result.length < maxAccounts) {
    result.push(...externalAccounts.slice(0, maxAccounts - result.length))
  }

  return result
}

const priorityMap: { [key in AccountType]?: number } = {
  [AccountType.CHECKING]: 1,
  [AccountType.SAVINGS]: 2,
  [AccountType.CREDIT_CARD]: 3,
}

const getPriority = (accountType: AccountType) => {
  return priorityMap[accountType] ?? accountType + 100
}

const sortByTypePriority = (list: Account[]) => {
  return list.sort((a, b) => {
    const aPriority = getPriority(a.account_type)
    const bPriority = getPriority(b.account_type)
    const priorityDiff = aPriority - bPriority

    if (priorityDiff !== 0) return priorityDiff

    const aName = a.name ?? a.feed_name ?? ''
    const bName = b.name ?? b.feed_name ?? ''
    return aName.localeCompare(bName)
  })
}
